import React, { useMemo } from "react";
import { RouteComponentProps } from "@reach/router";
import BusinessUnderstanding from "./BusinessUnderstanding";
import InvestmentPerformance from "./InvestmentPerformance";
import NextMediaDollar from "./NextMediaDollar";
import ModelInsights from "./ModelInsights";
import GeographicAnalysis from "./GeographicAnalysis";
import { FullPageSpinner } from "../../Components";

interface ResultsProps {
  groupBy: string;
  outcomeVariable?: "revenue" | "kpiVolume";
  isLoading: boolean;
  summaryMetricsRows: any[];
  mapeByGeoRows: any[];
  fitRows: any[];
  mediaSpendRows: any[];
  channelBreakoutMap: { [key: string]: any };
  geoNames: { [key: string]: string };
  attributionOverTimeRows: any[];
  roiTableRows: any[];
  responseCurvesRows: any[];
  adstockRows: any[];
  attributionByGeoRows: any[];
}

const Results = React.memo(
  ({
    groupBy,
    outcomeVariable,
    isLoading,
    summaryMetricsRows,
    mapeByGeoRows,
    fitRows,
    mediaSpendRows,
    channelBreakoutMap,
    geoNames,
    attributionOverTimeRows,
    roiTableRows,
    responseCurvesRows,
    adstockRows,
    attributionByGeoRows,
  }: ResultsProps & RouteComponentProps) => {
    const maxDimension: string = useMemo(() => {
      let maxDimensionIndex = 0;
      for (let breakoutName in channelBreakoutMap) {
        if (!breakoutName) {
          continue;
        }
        const breakoutDimensions = channelBreakoutMap[breakoutName];
        for (let dimensionKey in breakoutDimensions) {
          if (!dimensionKey || dimensionKey === "channel") {
            continue;
          }
          if (dimensionKey === "brand_nonbrand" && breakoutDimensions[dimensionKey]) {
            maxDimensionIndex = Math.max(maxDimensionIndex, 3);
            break;
          }
          if (dimensionKey === "tactic" && breakoutDimensions[dimensionKey]) {
            maxDimensionIndex = Math.max(maxDimensionIndex, 2);
            continue;
          }
          if (dimensionKey === "platform" && breakoutDimensions[dimensionKey]) {
            maxDimensionIndex = Math.max(maxDimensionIndex, 1);
            continue;
          }
        }
      }

      switch (maxDimensionIndex) {
        case 3:
          return "Brand/Non-Brand";
        case 2:
          return "Tactic";
        case 1:
          return "Platform";
        default:
          return "Channel";
      }
    }, [channelBreakoutMap]);

    return !isLoading ? (
      <div>
        <BusinessUnderstanding
          groupBy={groupBy}
          outcomeVariable={outcomeVariable!}
          summaryMetricsRows={summaryMetricsRows}
          mapeByGeoRows={mapeByGeoRows}
          fitRows={fitRows}
          mediaSpendRows={mediaSpendRows}
          channelBreakoutMap={channelBreakoutMap}
          geoNames={geoNames}
        />
        <InvestmentPerformance
          groupBy={groupBy}
          outcomeVariable={outcomeVariable!}
          attributionOverTimeRows={attributionOverTimeRows}
          channelBreakoutMap={channelBreakoutMap}
        />
        <NextMediaDollar
          roiTableRows={roiTableRows}
          channelBreakoutMap={channelBreakoutMap}
          outcomeVariable={outcomeVariable!}
          maxDimension={maxDimension}
        />
        <ModelInsights
          outcomeVariable={outcomeVariable!}
          responseCurvesRows={responseCurvesRows}
          adstockRows={adstockRows}
          channelBreakoutMap={channelBreakoutMap}
          maxDimension={maxDimension}
        />
        <GeographicAnalysis
          groupBy={groupBy}
          outcomeVariable={outcomeVariable!}
          attributionByGeoRows={attributionByGeoRows}
          geoNames={geoNames}
          channelBreakoutMap={channelBreakoutMap}
        />
      </div>
    ) : (
      <FullPageSpinner />
    );
  }
);

export default Results;
